import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BonsaiLogo, BonsaiMonogram } from "@/components/BonsaiLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-navy text-white relative overflow-hidden">
        <BonsaiLogo size={30} dark />
        <div className="relative z-10">
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">
            Configuratore<br />offerte commerciali
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-md">
            Costruisci offerte su misura per i tuoi clienti con moduli, licenze e
            add-on della piattaforma Bonsai. Genera preventivi professionali in pochi click.
          </p>
        </div>
        <p className="text-white/40 text-sm relative z-10">Bonsai Srls — Prato (PO)</p>
        <div className="absolute -right-24 -bottom-24 opacity-10">
          <BonsaiMonogram size={420} />
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 bg-secondary">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <BonsaiLogo size={30} />
          </div>
          <div className="bg-white border border-border rounded-2xl p-8">
            <h2 className="text-2xl font-extrabold text-navy">Accedi</h2>
            <p className="text-muted-foreground text-sm mt-1 mb-6">
              Entra nel tuo pannello commerciale.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" value={email} required
                  data-testid="login-email-input"
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@agenzia.it" className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password" type="password" value={password} required
                  data-testid="login-password-input"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="mt-1.5"
                />
              </div>
              {error && (
                <p data-testid="login-error" className="text-sm text-destructive font-semibold">
                  {error}
                </p>
              )}
              <Button
                type="submit" disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 text-base font-bold"
              >
                {loading ? "Accesso..." : "Accedi"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-accent font-semibold hover:underline">
                Password dimenticata?
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
